/**
 * pendingQueries — a tiny module-level store that tracks data queries which have
 * NOT yet received their first result (the transition `isPending → success|error`).
 *
 * It exists so a GLOBAL spinner (near the "Festipod" title) can turn while AT
 * LEAST ONE query is still waiting for its first result, and stop ONLY once EVERY
 * in-flight query has resolved. It is deliberately independent of React provider
 * order: it is a plain module singleton, driven by `useShapeQuery` (see B) and
 * observed by `usePendingQueries` via `useSyncExternalStore`.
 *
 * Contract:
 *  - `beginQuery(id)`   — a query enters the "waiting for first result" state.
 *  - `resolveQuery(id)` — that query got its first result (or errored, or its
 *    component unmounted while still pending). Removing an absent id is a no-op.
 *  - The store is a Set of ids, so `beginQuery(id)` twice does NOT double-count and
 *    `resolveQuery(id)` twice is harmless — this is what makes it safe under React
 *    StrictMode's symmetric double-invocation of effects, and lets a query that
 *    re-enters `isPending` (e.g. an identity/scope switch) begin again cleanly.
 */

import { useSyncExternalStore } from 'react';

const pending = new Set<string>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** Mark a query as waiting for its first result. Idempotent per id. */
export function beginQuery(id: string): void {
  if (pending.has(id)) return;
  pending.add(id);
  emit();
}

/** Mark a query as having received its first result. No-op if absent. */
export function resolveQuery(id: string): void {
  if (!pending.delete(id)) return;
  emit();
}

/** Number of queries currently waiting for their first result. */
export function getPendingCount(): number {
  return pending.size;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribe a React component to the pending-query count. Re-renders whenever the
 * count changes. `getPendingCount` returns a primitive (number), so the snapshot
 * is stable between real changes — no render loop.
 */
export function usePendingQueries(): number {
  return useSyncExternalStore(subscribe, getPendingCount, getPendingCount);
}
